import React, { useEffect, useRef } from 'react'
import Chart from 'chart.js/auto'
import './Graph.scss'
import zoomPlugin from 'chartjs-plugin-zoom'
import { ChartEvent } from 'chart.js'

Chart.register(zoomPlugin)

const resetZoomPlugin = {
  id: 'resetZoomPlugin',
  resetButtonImage: new Image(),
  imageLoaded: false,
  afterDraw(chart: Chart) {
    if (!this.imageLoaded) {
      this.resetButtonImage.onload = () => {
        this.imageLoaded = true
        chart.draw() // Redraw the chart once the image is loaded
      }
      this.resetButtonImage.src = 'refresh.png'
      return // Skip drawing until the image is loaded
    }
    const { ctx } = chart
    const { top, right } = chart.chartArea
    const size = 20

    // Draw a simple rectangle as the reset button
    ctx.save()
    ctx.fillStyle = 'rgba(255, 255, 255, 0.8)'
    ctx.fillRect(right - size, top, size, size) // Positioned in the top-right corner

    // Draw the reset image (now cached)
    ctx.drawImage(this.resetButtonImage, right - size, top, size, size)

    ctx.restore()
  },
  beforeEvent(chart: Chart, args: { event: ChartEvent }) {
    const { event } = args
    const { top, right } = chart.chartArea
    const size = 20

    // Check if the event was a click event and within the button area
    if (
      event.type === 'click' &&
      event.x &&
      event.y &&
      event.x >= right - size &&
      event.x <= right &&
      event.y >= top &&
      event.y <= top + size
    ) {
      chart.resetZoom() // Call resetZoom if the click is within the reset button area
    }
  },
}

Chart.register(zoomPlugin, resetZoomPlugin)

interface AnimatedGraphProps {
  data: { x: number; y: number }[]
  title: string
  yAxisMin?: number
  yAxisMax?: number
  currentTime?: number // Current video time in seconds
}

function AnimatedGraph({
  data,
  title,
  yAxisMin,
  yAxisMax,
  currentTime,
}: AnimatedGraphProps): React.ReactElement | null {
  const chartRef = useRef<HTMLCanvasElement | null>(null)
  const chartInstance = useRef<Chart | null>(null)
  const durationString = process.env.CLIPS_DURATION
  const Duration = durationString ? parseInt(durationString, 10) : 60000
  const videoDuration = Duration / 1000

  useEffect(() => {
    if (!chartRef.current) {
      return
    }

    if (chartInstance.current) {
      chartInstance.current.destroy()
    }

    const xMax = data.length - 1

    chartInstance.current = new Chart(chartRef.current, {
      type: 'line',
      data: {
        datasets: [
          {
            borderColor: '#0bcfbe',
            borderWidth: 1,
            pointRadius: 0,
            label: title,
            data,
          },
          {
            borderColor: 'grey', // Color of the vertical line
            borderWidth: 1,
            pointRadius: 0,
            borderDash: [5, 5],
            label: 'Current Time',
            data: [
              { x: 0, y: yAxisMin || 0 },
              { x: 0, y: yAxisMax || 1000 },
            ],
          },
        ],
      },
      options: {
        animation: false,
        scales: {
          x: {
            type: 'linear',
            min: 0,
            max: xMax,
            display: false,
          },
          y: {
            min: yAxisMin || 0,
            max: yAxisMax || 1000,
          },
        },
        plugins: {
          legend: {
            display: false,
          },
          title: {
            display: true,
            text: title,
            padding: 10,
            font: {
              size: 17,
            },
          },
          zoom: {
            zoom: {
              wheel: {
                enabled: true,
              },
              pinch: {
                enabled: true,
              },
              mode: 'xy',
            },
            pan: {
              enabled: true,
              mode: 'xy',
            },
          },
        },
      },
      plugins: [resetZoomPlugin],
    })
  }, [data, title, yAxisMin, yAxisMax])

  // Update the position of the vertical line when currentTime updates
  useEffect(() => {
    if (chartInstance.current && currentTime != null) {
      let pointIndex = (currentTime / videoDuration) * (data.length - 1)
      pointIndex = Math.max(0, Math.min(pointIndex, data.length - 1)) // Ensure the index is within bounds
      chartInstance.current.data.datasets[1].data = [
        { x: pointIndex, y: yAxisMin || 0 },
        { x: pointIndex, y: yAxisMax || 1000 },
      ]
      chartInstance.current.update()
    }
  }, [currentTime, data.length, yAxisMin, yAxisMax, videoDuration])

  return <canvas ref={chartRef} className="animated-graph" />
}

export default AnimatedGraph
